﻿import { IResourceProvider } from 'common/resourceprovider';
import { dateTimeTransmissionFormat, longDateTimeFormat } from 'common/functions/date';
import { format } from 'common/functions/string';
import * as moment from 'moment';

export interface IHistoryInfo {
    workflowInstanceId: string;
    workItemId: string;
    userDisplayName: string;
    stepName: string;
    stepTitle: string;
    dateTime: string;

    // ReSharper disable once UnusedParameter
    summary: (resources: IResourceProvider) => string;
}

export class HistoryInfo implements IHistoryInfo {
    workflowInstanceId: string;
    workItemId: string;
    userDisplayName: string;
    stepName: string;
    stepTitle: string;
    dateTime: string;

    baseMoment = moment.utc('0001-01-01 00:00:00');

    private timeSpanRegEx = /(\d+)\.((?:\d+):(?:60|(?:[0-5][0-9])):(?:60|(?:[0-5][0-9]))\.(?:\d+))/ig;

    // ReSharper disable once UnusedParameter
    summary = (resources: IResourceProvider) => {
        return 'HistoryInfo.Summary';
    };

    protected dateTimeToLongFormat = (resources: IResourceProvider) => {
        return moment(this.dateTime, dateTimeTransmissionFormat()).format(longDateTimeFormat(resources));
    };

    formatTimeSpan(resources: IResourceProvider, timeSpan: string) {
        var regexResult = this.timeSpanRegEx.exec(timeSpan);
        var timeSpanDateString = (regexResult === undefined || regexResult === null)
            ? '0001-01-01 ' + timeSpan
            : (regexResult.length > 2) ? moment.utc('0001-01-01', 'YYYY-MM-DD').add(regexResult[1], 'days').format('YYYY-MM-DD ') + regexResult[2] : '0001-01-01 ' + regexResult[1];
        var dateSpan = moment.utc(timeSpanDateString, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm:ss.SSSSSSSS']);

        var timeSpanDiff = dateSpan.diff(this.baseMoment, 'seconds');
        if (timeSpanDiff < 1) {
            return resources.getCoreString('LblLessThanASecond');
        }
        var descriptionElements: string[] = [];

        descriptionElements.push(this.addNonZeroElement(resources, 'day', dateSpan.date() - 1)); //descriptionElements.push(this.addNonZeroElement('day', timeSpan.Days));
        descriptionElements.push(this.addNonZeroElement(resources, 'hour', dateSpan.hour())); //descriptionElements.push(this.addNonZeroElement('hour', timeSpan.Hours));
        descriptionElements.push(this.addNonZeroElement(resources, 'minute', dateSpan.minute())); //descriptionElements.push(this.addNonZeroElement("minute", timeSpan.Minutes));
        descriptionElements.push(this.addNonZeroElement(resources, 'second', dateSpan.second())); //descriptionElements.push(this.addNonZeroElement('second', timeSpan.Seconds));

        return descriptionElements.filter(x => (x !== '')).join(', ');
    }

    private addNonZeroElement(resources: IResourceProvider, elementName: string, elementValue: number) {
        if (elementValue <= 0) {
            return '';
        }

        var elementLabel = '';

        switch (elementName) {
            case 'day':
                elementLabel = (elementValue === 1 ? resources.getCoreString('LblTimeSpanDay') : resources.getCoreString('LblTimeSpanDays'));
                break;
            case 'hour':
                elementLabel = (elementValue === 1 ? resources.getCoreString('LblTimeSpanHour') : resources.getCoreString('LblTimeSpanHours'));
                break;
            case 'minute':
                elementLabel = (elementValue === 1 ? resources.getCoreString('LblTimeSpanMinute') : resources.getCoreString('LblTimeSpanMinutes'));
                break;
            case 'second':
                elementLabel = (elementValue === 1 ? resources.getCoreString('LblTimeSpanSecond') : resources.getCoreString('LblTimeSpanSeconds'));
                break;
        }

        return elementValue + ' ' + elementLabel;
    }
}

export class HistoryInfoArrived extends HistoryInfo implements IHistoryInfo {
    summary = (resources: IResourceProvider): string => {
        return format(resources.getCoreString('MsgWorkItemAvailableForClaimAt'), this.dateTimeToLongFormat(resources));
    };
}

export class HistoryInfoAssigned extends HistoryInfo implements IHistoryInfo {
    toUser: string;

    summary = (resources: IResourceProvider): string => {
        return format(resources.getCoreString('MsgWorkItemManuallyAssignedToByAt'), this.toUser, this.userDisplayName, this.dateTimeToLongFormat(resources));
    };
}

export class HistoryInfoClaimCancelled extends HistoryInfo implements IHistoryInfo {
    claimDuration: string;

    summary = (resources: IResourceProvider): string => {
        return format(resources.getCoreString('MsgWorkItemClaimCancelledByAt'), this.userDisplayName, this.dateTimeToLongFormat(resources), this.formatTimeSpan(resources, this.claimDuration));
    };
}

export class HistoryInfoClaimed extends HistoryInfo implements IHistoryInfo {
    summary = (resources: IResourceProvider): string => {
        return format(resources.getCoreString('MsgWorkItemClaimedByAt'), this.userDisplayName, this.dateTimeToLongFormat(resources));
    };
}

export class HistoryInfoClaimForceCancelled extends HistoryInfo implements IHistoryInfo {
    claimUser: string;
    claimDuration: string;

    summary = (resources: IResourceProvider): string => {
        return format(resources.getCoreString('MsgWorkItemFociblyCancelledByAt'), this.userDisplayName, this.dateTimeToLongFormat(resources), this.claimUser, this.formatTimeSpan(resources, this.claimDuration));
    };
}

export class HistoryInfoCreated extends HistoryInfo implements IHistoryInfo {
    summary = (resources: IResourceProvider): string => {
        return format(resources.getCoreString('MsgWorkItemCreatedAt'), this.dateTimeToLongFormat(resources));
    };
}

export class HistoryInfoSubmitted extends HistoryInfo implements IHistoryInfo {
    claimDuration: string;

    summary = (resources: IResourceProvider): string => {
        return format(resources.getCoreString('MsgWorkItemSubmittedByAt'), this.userDisplayName, this.dateTimeToLongFormat(resources), this.formatTimeSpan(resources, this.claimDuration));
    };
}

export class HistoryInfoTransferred extends HistoryInfo implements IHistoryInfo {
    fromUser: string;
    toUser: string;
    claimDuration: string;

    summary = (resources: IResourceProvider): string => {
        return format(resources.getCoreString('MsgWorkItemTransferredFromToByAt'), this.fromUser, this.toUser, this.userDisplayName, this.dateTimeToLongFormat(resources), this.formatTimeSpan(resources, this.claimDuration));
    };
}